import { produce } from "immer";
import { AnyAction } from "redux";
import { SagaIterator } from "redux-saga";
import { call, put, SagaGenerator, select, takeLatest, takeMaybe } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { Model, ModelActions, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { FormActivity, FormEngineSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { FormModel, isFormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import {
	DocumentModel,
	DocumentRtServiceFactory,
	DocumentServiceFactory,
	IGeneratedCodeAccessor
} from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { getAllFVTasks, getFVManifestMap } from "../../../app/slices/fachverfahren/selectors";
import { assertNonNullable } from "../../../utilities/assert";
import { modelPathContainsRepetition } from "../../../utilities/modelHelpers";
import { entityInstancePathFromModelPath } from "../../../utilities/pathHelpers";
import { isNonNullable } from "../../../utilities/typeGuardUtils";
import { DESCRIPTOR_KEY_ARCHIVED_ANTRAG } from "../../../utilities/variables/activityConstants";
import { getDocument, hasRole } from "../../../utilities/rpcHelper";
import { isGatewayTimeoutError } from "../../../utilities/restHelper";
import { isDocumentWithProcessMetadata, ProcessMetadataContainingDocument } from "../../workflows/data/processMetadata";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { RESOURCE_KEYS } from "../../../localization";
import { getDocumentWithPermission, isDocumentContainingA12wf } from "../../../utilities/workflowUtils";
import { reloadWiedervorlagen } from "../../wiedervorlagen/actions";
import { WIEDERVORLAGEN_OVERVIEW_ACTIVITY } from "../../wiedervorlagen/constants";
import { dataModels } from "../../wiedervorlagen/moduleResources";
import { FRISTEN_ACTIVITY } from "../../fristen/constants";
import { createCustomNotificationAction } from "../../../app/actions";
import { convertAppIdToKey } from "../../../utilities/applicationKeyUtils";
import { hasUpdateDocumentPermission } from "../../../utilities/permissionUtils";
import { selectHasNeededPermissions } from "../../../utilities/user";
import { lockProcess, startLockProcessPolling, stopLockProcessPolling } from "../../processFormEngine/store/actions";
import { clearOperationLock } from "../../../app/slices/operation/reducers";
import { DOCUMENT_LOCK_GROUP, hasBetrachterRoleForDocument } from "../../../utilities/lockProcessUtils";

import {
	ANTRAG_DETAIL_ACTIVITY_ID,
	OpenDocumentPayload,
	openTaskDocument,
	openTaskDocumentAndShowWiedervorlagen,
	TASK_DETAIL_ACTIVITY_ID,
	taskDocumentLoaded
} from "../store/actions";

import { putTaskPermissionTimeoutNotification, setPermissionInTask } from "./checkTaskPermissionSaga";

const logger = LoggerFactory.getLogger("modulf");

/**
 * Saga um die Action `openDocument` zu handlen.
 * Wenn der Prozess Task der initiale Task ist, wird nur eine Activity
 * zum Öffnen des Formulars gestartet.
 * Wenn der Prozess Task nicht der initiale Task ist, wird neben der
 * Activity zum Öffnen des Bearbeitungsformulars eine zweite Activity
 * zur Anzeige des Antragsformulars geöffnet
 */
export function* openTaskDocumentSaga(): SagaIterator<void> {
	yield* takeLatest(
		openTaskDocument,
		function* handleOpenDocument(action: Action<OpenDocumentPayload>): SagaGenerator<void> {
			let document = action.payload.document;
			let hasPermission = action.payload.hasTaskPermission;
			if (!document || !isDocumentContainingA12wf(document)) {
				try {
					const result = yield* call(
						fetchDocumentWithOptionalPermission,
						action.payload.instance,
						hasPermission
					);
					if (!result) {
						yield* put(
							createCustomNotificationAction({
								title: { key: RESOURCE_KEYS.features.notification.title.error },
								message: { key: RESOURCE_KEYS.features.notification.error.process.unauthorized },
								icon: "error",
								severity: "error"
							})
						);
						yield* put(ApplicationActions.setBusy(false));
						return;
					}
					document = result.document;
					hasPermission = result.hasPermission;
				} catch (e) {
					if (isGatewayTimeoutError(e)) {
						yield* put(
							createCustomNotificationAction({
								title: { key: RESOURCE_KEYS.features.notification.title.error },
								message: { key: RESOURCE_KEYS.wf.notification.error.gatewayTimeout },
								icon: "error",
								severity: "error",
								componentId: "NotificationWithButton",
								group: "task-open-timeout",
								button: {
									action: openTaskDocument(action.payload),
									label: { key: RESOURCE_KEYS.wf.notification.error.gatewayTimeoutRetry }
								}
							})
						);
						yield* put(ApplicationActions.setBusy(false));
						return;
					}
					yield* call(
						putErrorNotification,
						RESOURCE_KEYS.features.notification.error.process.unauthorized,
						e
					);
					yield* put(ApplicationActions.setBusy(false));
					return;
				}
			}
			if (!isDocumentWithProcessMetadata(document)) {
				yield* put(ApplicationActions.setBusy(false));
				return;
			}

			const isBetrachter = yield* call(hasBetrachterRoleForDocument, document);
			const taskDetailActivityExists = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
			if (!isBetrachter && !taskDetailActivityExists) {
				yield* put(stopLockProcessPolling({}));
				yield* put(clearOperationLock({ group: DOCUMENT_LOCK_GROUP }));
				yield* put(lockProcess({ docRef: document.id }));
				yield* put(startLockProcessPolling({ docRef: document.id }));
			}

			const modelId = action.payload.instance.split("/")[0];
			const {
				root: {
					processMetadata: {
						task: { formKey: taskFormModelId, id: taskId },
						processDefinitionId,
						fachverfahrenHistory,
						isFinished,
						fachverfahren: { id: fachverfahrenId, key: fachverfahrenKey }
					}
				}
			} = document;

			const initialTaskFormModelId = fachverfahrenId + "_Antrag_F";

			const initiatingActivityId = yield* call(
				jumpToMainActivityContext,
				action.payload.initiatingActivityId,
				fachverfahrenKey
			);
			// Felder aus dem Deskriptor der initiierenden Activity weitertragen
			let sourceDescriptor: Record<string, string | undefined> = {};
			if (initiatingActivityId) {
				const initiatingActivity = yield* select(ActivitySelectors.activityById(initiatingActivityId));
				if (!initiatingActivity) {
					throw new Error("Initiating activity " + initiatingActivityId + " not found");
				}

				sourceDescriptor = {
					...initiatingActivity.descriptor,
					fachverfahren: fachverfahrenId,
					processes: processDefinitionId,
					instance: action.payload.instance
				};
				delete sourceDescriptor["model"]; // Den Modell-Identifier nicht weitertragen
			}
			const data = yield* call(prepareDocument, document, modelId, taskFormModelId);
			if (initialTaskFormModelId && taskFormModelId !== initialTaskFormModelId) {
				yield* call(
					showAntragFormActivity,
					sourceDescriptor,
					initialTaskFormModelId,
					modelId,
					taskId,
					isFinished,
					initiatingActivityId,
					data
				);
			}

			const initialScreenTaskKey = fachverfahrenHistory?.slice().pop()?.entry;
			const allTasks = yield* select(getAllFVTasks(fachverfahrenId));
			const screenToUse =
				allTasks.flatMap(t => t.key).find(key => key.taskId === initialScreenTaskKey)?.screenName ??
				initialScreenTaskKey;
			const activityDescriptor: Activity.Descriptor = {
				view: "Task",
				formModel: taskFormModelId,
				modelId,
				taskId
			};
			const archivedActivityDescriptor: Activity.Descriptor = {
				model: modelId,
				key: DESCRIPTOR_KEY_ARCHIVED_ANTRAG
			};
			const showTaskFormActionPayload = {
				activityDescriptor: {
					...sourceDescriptor,
					...(isFinished ? archivedActivityDescriptor : activityDescriptor)
				},
				initiatingActivityId,
				activityId: TASK_DETAIL_ACTIVITY_ID,
				...(isFinished
					? {
							slices: {
								uiState: {
									readonly: true,
									...(screenToUse
										? {
												screenLocation: [
													{
														locationPath: [
															{
																elementName: screenToUse
															}
														],
														path: []
													}
												]
											}
										: {})
								}
							}
						}
					: { slices: { uiState: { readonly: true } } })
			};

			yield* put(
				ActivityActions.create({
					...showTaskFormActionPayload,
					data,
					loadingState: data ? "loaded" : "missing"
				})
			);

			yield* call(applyInitialFormModelValues, showTaskFormActionPayload.activityId, modelId, taskFormModelId);

			yield* put(taskDocumentLoaded({ document, activityId: TASK_DETAIL_ACTIVITY_ID }));
			try {
				if (!hasPermission || !hasPermission.isLoaded) {
					const resolvedPermission = yield* call(hasRole, action.payload.instance);
					hasPermission = { isLoaded: true, hasPermission: resolvedPermission };
				}
			} catch (error) {
				yield* call(putTaskPermissionTimeoutNotification, error);
				yield* put(ApplicationActions.setBusy(false));
				return;
			}
			yield* call(setPermissionInTask, !!hasPermission.hasPermission, TASK_DETAIL_ACTIVITY_ID);
			yield* put(reloadWiedervorlagen({ activityId: TASK_DETAIL_ACTIVITY_ID }));
			yield* put(ApplicationActions.setBusy(false));
		}
	);
}

function* fetchDocumentWithOptionalPermission(
	instance: string,
	hasPermission: { isLoaded?: boolean; hasPermission?: boolean } | undefined
): SagaGenerator<
	| {
			document: ProcessMetadataContainingDocument;
			hasPermission: { isLoaded: boolean; hasPermission?: boolean };
	  }
	| undefined
> {
	if (!hasPermission || !hasPermission.isLoaded) {
		const result = yield* call(getDocumentWithPermission, instance);
		if (!result.docSpec || !isDocumentWithProcessMetadata(result.docSpec.document)) {
			return undefined;
		}
		const document = produce(result.docSpec.document, draft => {
			draft.id = instance;
		});
		const permission =
			result.hasPermission !== undefined
				? { isLoaded: true, hasPermission: result.hasPermission }
				: { isLoaded: false };
		return { document, hasPermission: permission };
	} else {
		const fetchedDocument = (yield* call(getDocument, instance))?.document;
		if (!isDocumentWithProcessMetadata(fetchedDocument)) {
			return undefined;
		}
		const document = produce(fetchedDocument, draft => {
			draft.id = instance;
		});
		return { document, hasPermission: { isLoaded: true, hasPermission: hasPermission.hasPermission } };
	}
}

function* prepareDocument(
	document: Activity.Data.Document,
	modelId: string,
	taskFormModelId: string
): SagaGenerator<FormActivity.Data.SingleDocumentData> {
	const documentService = new DocumentServiceFactory().getDocumentService();
	const { validationProvider, documentModel } = yield* call(loadModels, modelId, taskFormModelId);
	const precomputedDocument = documentService.parseDates(document, documentModel);
	let result = isDocumentWithProcessMetadata(precomputedDocument) ? precomputedDocument : document;
	const rtService = DocumentRtServiceFactory.createDocumentRtService(validationProvider);
	const { modelId: ignore, ...documentWithoutModelId } = result;
	const computed = rtService.compute(documentWithoutModelId).appliedTo(result);
	result = isDocumentWithProcessMetadata(computed) ? computed : result;
	return { document: { ...result, modelId } };
}

function* applyInitialFormModelValues(activityId: string, documentModelId: string, formModelId: string) {
	const data = yield* select(ActivitySelectors.data(activityId));
	const taskFormModel = yield* select(ModelSelectors.modelByName(formModelId, isFormModel));
	const documentModel = yield* select(ModelSelectors.modelByName(documentModelId, Model.isDocumentModel));
	if (!(taskFormModel && documentModel && FormActivity.Data.SingleDocumentData.isInstance(data))) {
		return;
	}
	const documentService = new DocumentServiceFactory().getDocumentService();
	const initializedData = produce(data, draft => {
		Object.values(taskFormModel.content.fieldConfiguration.fieldMap)
			.filter(isNonNullable)
			.filter(
				it => it.initialValueTyped !== undefined && !modelPathContainsRepetition(documentModel, it.elementPath)
			)
			.map(it => ({ path: entityInstancePathFromModelPath(it.elementPath), value: it.initialValueTyped }))
			.filter(it => documentService.getAssignedObject(draft.document, it.path) === undefined)
			.forEach(it => {
				draft.document = {
					...documentService.updateEntityInstance(draft.document, it.path, it.value, documentModel),
					id: draft.document.id,
					modelId: draft.document.modelId
				};
			});
	});
	yield* put(ActivityActions.setData({ activityId: activityId, data: initializedData }));
}

/**
 * Starte eine zweite Activity, wenn der aktuelle Prozessschritt nicht der initiale Prozessschritt ist,
 * um den Antrag anzuzeigen.
 * Der initiale Prozessschritt wird über das initiale Form-Model definiert.
 * Wartet anschließend darauf, dass ein Set-Data für die Activity ausgeführt wird.
 */
function* showAntragFormActivity(
	sourceDescriptor: Record<string, string | undefined>,
	initialTaskFormModel: string,
	modelId: string,
	taskId: string,
	isFinished: boolean,
	initiatingActivityId: string | undefined,
	data: FormActivity.Data.SingleDocumentData | undefined
) {
	const showAntragFormActivityDescriptor: Activity.Descriptor = {
		...sourceDescriptor,
		view: "Task",
		formModel: initialTaskFormModel,
		modelId,
		taskId,
		side: "left",
		editable: "true"
	};

	const archivedFormActivityDescriptor: Activity.Descriptor = {
		...sourceDescriptor,
		formModel: initialTaskFormModel
	};

	const showAntragFormAction = ActivityActions.create({
		activityDescriptor: { ...(isFinished ? archivedFormActivityDescriptor : showAntragFormActivityDescriptor) },
		initiatingActivityId,
		activityId: ANTRAG_DETAIL_ACTIVITY_ID,
		slices: { uiState: { readonly: true } },
		loadingState: "loaded",
		data
	});

	yield* put(showAntragFormAction);

	yield* put(reloadWiedervorlagen({ activityId: ANTRAG_DETAIL_ACTIVITY_ID }));
}

/**
 * Springt zu der zum Formmodell gehörigen Top-Level-Activity.
 * @returns Die ID der neu gestarteten Top-Level-Activity oder die ursprüngliche initiatingActivityId, falls die TLA nicht gewechselt werden musste
 */
function* jumpToMainActivityContext(
	initiatingActivityId: string | undefined,
	fachverfahrenKey: string
): SagaGenerator<string | undefined> {
	const currentOverviewActivityDescriptor = yield* call(getOverviewActivityDescriptor, fachverfahrenKey);
	if (!currentOverviewActivityDescriptor) {
		return initiatingActivityId;
	}

	if (initiatingActivityId) {
		const initiatingActivity = yield* select(ActivitySelectors.activityById(initiatingActivityId));
		if (initiatingActivity && isTargetActivity(initiatingActivity, currentOverviewActivityDescriptor)) {
			return initiatingActivityId;
		}
	}

	yield* put(ApplicationActions.startMainActivityRequested({ descriptor: currentOverviewActivityDescriptor }));
	const activity = yield* takeMaybe(ActivityActions.push);
	return activity.payload.activity.id;
}

function isTargetActivity(activity: Activity, descriptor: Activity.Descriptor) {
	return Object.keys(descriptor).every(key => descriptor[key] === activity.descriptor[key]);
}

/**
 * Es gibt für alle Versionen eines Fachverfahrens nur eine Übersicht, bzw. das App-Model kennt nur eine Übersicht.
 * Der Activity-Descriptor der Übersicht basiert immer auf der neusten Version.
 * Wenn eine alte Version geöffnet wird, muss die aktuelle Übersicht für das Fachverfahren herausgesucht werden.
 * Das macht diese Methode, indem das Fachverfahrens-Prefix aus der Model-ID extrahiert wird und dann die Übersicht passend zu
 * dem Fachverfahrens-Prefix im App-Model gesucht wird.
 */
function* getOverviewActivityDescriptor(fachverfahrenKey: string): SagaGenerator<Activity.Descriptor | undefined> {
	const applicationModel = yield* select(ModelSelectors.applicationModel());
	const manifests = yield* select(getFVManifestMap); // from fachverfahren/selectors.ts
	return applicationModel.content.modules
		.map(it => it.menu?.initialActivity?.descriptor)
		.find(it => it?.fachverfahren && manifests[it.fachverfahren]?.identification.key === fachverfahrenKey);
}

function* loadModels(
	documentModelId: string,
	taskFormModelId: string
): SagaGenerator<{ formModel: FormModel; documentModel: DocumentModel; validationProvider: IGeneratedCodeAccessor }> {
	const documentAndValidationModel = yield* select(
		ModelSelectors.modelByName(documentModelId, Model.isDocumentAndValidationModel)
	);
	const formModel = yield* select(ModelSelectors.modelByName(taskFormModelId, isFormModel));
	if (documentAndValidationModel && formModel) {
		const { generatedCodeAccessor: validationProvider, ...documentModelWithoutValidation } =
			documentAndValidationModel;
		return { formModel, documentModel: documentModelWithoutValidation, validationProvider };
	}
	const modelDescriptors: Model.Descriptor[] = [];
	if (!formModel) {
		modelDescriptors.push({ modelType: "form", name: taskFormModelId });
	}
	if (!documentAndValidationModel) {
		modelDescriptors.push({ modelType: "document", name: documentModelId });
	}
	const modelsToLoad = modelDescriptors.map(e => e.name);

	logger.info(`Models ${modelsToLoad} not loaded. Will load now.`);
	yield* put(ModelActions.loadModels(modelDescriptors));
	yield* takeMaybe(
		(a: AnyAction) =>
			ModelActions.setModels.match(a) && modelsToLoad.every(it => Object.keys(a.payload).includes(it))
	);
	const loadedDocumentModel = yield* select(
		ModelSelectors.modelByName(documentModelId, Model.isDocumentAndValidationModel)
	);
	const loadedFormModel = yield* select(ModelSelectors.modelByName(taskFormModelId, isFormModel));
	assertNonNullable(loadedFormModel);
	assertNonNullable(loadedDocumentModel);
	const { generatedCodeAccessor: validationProvider, ...documentModelWithoutValidation } = loadedDocumentModel;
	return { formModel: loadedFormModel, documentModel: documentModelWithoutValidation, validationProvider };
}

/**
 * Saga um ein Dokument zu öffnen und anschließend automatisch die Wiedervorlagen-Übersicht anzuzeigen.
 */
export function* openTaskDocumentAndShowWiedervorlagenSaga(): SagaIterator<void> {
	yield* takeLatest(
		openTaskDocumentAndShowWiedervorlagen,
		function* (action: Action<OpenDocumentPayload>): SagaGenerator<void> {
			// Dispatch the standard openTaskDocument action
			yield* put(openTaskDocument(action.payload));

			// Wait for the task document to finish loading
			yield* takeMaybe(taskDocumentLoaded.match);

			// Now open the Wiedervorlagen overview
			const activity = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
			if (!activity) {
				return;
			}

			const sourceDescriptor = { ...(activity.descriptor ?? {}) } as Record<string, string | undefined>;
			const fvId = sourceDescriptor.fachverfahren;

			const data = yield* select(ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID));
			const processId =
				FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithProcessMetadata(data.document)
					? data.document.root.processMetadata.processId
					: undefined;

			if (!processId) {
				return;
			}

			const fvKey = fvId ? convertAppIdToKey(fvId) : undefined;
			const isFinished =
				FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithProcessMetadata(data.document)
					? data.document.root.processMetadata.isFinished
					: false;
			const taskUiState = yield* select(FormEngineSelectors.uiState(TASK_DETAIL_ACTIVITY_ID));
			const isTaskReadOnly = (taskUiState?.readonly ?? false) && !isFinished;
			const hasPermission = yield* select(selectHasNeededPermissions(hasUpdateDocumentPermission, fvKey));

			// Close fristen activity if open
			const fristenActivity = yield* select(ActivitySelectors.activityById(FRISTEN_ACTIVITY));
			if (fristenActivity) {
				yield* put(ActivityActions.cancel({ activityId: FRISTEN_ACTIVITY }));
			}

			// Close existing wiedervorlagen activity if open
			const existingWiedervorlagen = yield* select(
				ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY)
			);
			if (existingWiedervorlagen) {
				yield* put(ActivityActions.cancel({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
			}

			yield* put(
				ActivityActions.create({
					activityDescriptor: {
						fachverfahren: fvId,
						instance: undefined,
						model: dataModels.WIEDERVORLAGE_D,
						processId
					},
					initiatingActivityId: TASK_DETAIL_ACTIVITY_ID,
					activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY,
					slices: {
						uiState: {
							readonly: !hasPermission || isTaskReadOnly
						}
					}
				})
			);
		}
	);
}
